import { useEffect, useState } from "react";
import { Inbox, Mail, Building2, Phone, CalendarCheck, FileSpreadsheet } from "lucide-react";
import { api } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [audits, setAudits] = useState([]);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/meetings").then((r) => setMeetings(r.data)).catch(() => {});
    api.get("/ar-audits").then((r) => setAudits(r.data)).catch(() => {});
  }, []);

  const tabs = [
    { key: "leads", label: "Demo Requests", count: leads.length, icon: Inbox },
    { key: "meetings", label: "Booked Demos", count: meetings.length, icon: CalendarCheck },
    { key: "audits", label: "AR Audits", count: audits.length, icon: FileSpreadsheet },
  ];

  return (
    <div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter text-[#0C2A40]">Requests</h1>
      <p className="mt-2 text-[#51667A]">Everything captured from the marketing site — enquiries, booked demos and AR audits.</p>

      <div className="mt-8 flex gap-2 flex-wrap">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-mono text-[11px] tracking-[0.12em] uppercase transition-colors ${tab === t.key ? "bg-[#2880B8] text-white" : "bg-[#E7F1F8] text-[#2880B8]"}`}>
              <Icon size={14} /> {t.label} ({t.count})
            </button>
          );
        })}
      </div>

      {tab === "leads" && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="leads-list">
          {leads.length === 0 && <Empty label="No demo requests yet." />}
          {leads.map((l, i) => (
            <div key={i} data-testid="lead-card" className="bg-white rounded-2xl border border-[#D8E6F1] p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-lg font-semibold text-[#0C2A40]">{l.name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-[#51667A]">
                    <div className="flex items-center gap-2"><Mail size={13} className="text-[#2880B8]" /> {l.email}</div>
                    {l.company && <div className="flex items-center gap-2"><Building2 size={13} className="text-[#2880B8]" /> {l.company}</div>}
                    {l.phone && <div className="flex items-center gap-2"><Phone size={13} className="text-[#2880B8]" /> {l.phone}</div>}
                  </div>
                </div>
                {l.plan && <span className="font-mono text-[9px] tracking-[0.12em] uppercase text-[#2880B8] bg-[#E7F1F8] px-2 py-1 rounded-full">{l.plan}</span>}
              </div>
              {l.message && <p className="mt-4 pt-4 border-t border-[#F2F8FC] text-sm text-[#0C2A40] leading-relaxed">{l.message}</p>}
              <p className="mt-3 font-mono text-[10px] text-[#93A5B5]">{new Date(l.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "meetings" && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="meetings-list">
          {meetings.length === 0 && <Empty label="No demos booked yet." />}
          {meetings.map((m, i) => (
            <div key={i} data-testid="meeting-card" className="bg-white rounded-2xl border border-[#D8E6F1] p-6">
              <div className="flex items-start justify-between">
                <h3 className="font-display text-lg font-semibold text-[#0C2A40]">{m.name}</h3>
                <span className="font-mono text-[9px] tracking-[0.12em] uppercase text-[#2880B8] bg-[#E7F1F8] px-2 py-1 rounded-full">{m.date} · {m.time}</span>
              </div>
              <div className="mt-2 space-y-1 text-sm text-[#51667A]">
                <div className="flex items-center gap-2"><Mail size={13} className="text-[#2880B8]" /> {m.email}</div>
                {m.company && <div className="flex items-center gap-2"><Building2 size={13} className="text-[#2880B8]" /> {m.company}</div>}
              </div>
              <p className="mt-3 font-mono text-[10px] text-[#93A5B5]">Booked {new Date(m.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "audits" && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="audits-list">
          {audits.length === 0 && <Empty label="No AR audits uploaded yet." />}
          {audits.map((a, i) => (
            <div key={i} data-testid="audit-card" className="bg-white rounded-2xl border border-[#D8E6F1] p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-lg font-semibold text-[#0C2A40]">{a.name}</h3>
                  <div className="mt-1 text-sm text-[#51667A] flex items-center gap-2"><Mail size={13} className="text-[#2880B8]" /> {a.email}</div>
                </div>
                <span className="font-mono text-[9px] tracking-[0.12em] uppercase text-[#2880B8] bg-[#E7F1F8] px-2 py-1 rounded-full">{a.filename}</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <Stat v={money(a.total_ar)} l="Total AR" />
                <Stat v={`${a.over_90_pct}%`} l="Over 90d" danger />
                <Stat v={money(a.estimated_recoverable)} l="Recoverable" />
              </div>
              <p className="mt-3 font-mono text-[10px] text-[#93A5B5]">{new Date(a.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Empty = ({ label }) => (
  <div className="md:col-span-2 bg-[#F2F8FC] rounded-2xl border border-dashed border-[#D8E6F1] p-12 text-center text-[#93A5B5] font-mono text-xs uppercase tracking-[0.15em]">{label}</div>
);

const Stat = ({ v, l, danger }) => (
  <div className={`rounded-xl p-3 ${danger ? "bg-red-50" : "bg-[#F2F8FC]"}`}>
    <div className={`font-mono text-lg font-bold ${danger ? "text-red-600" : "text-[#0C2A40]"}`}>{v}</div>
    <div className="font-mono text-[9px] tracking-[0.1em] uppercase text-[#93A5B5]">{l}</div>
  </div>
);
